import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { GroupBase, ValueContainerProps } from 'react-select';
import { components } from 'react-select';

import styled from 'styled-components';

//constants
import aampeTheme from '../../../constants/aampeTheme';

import { truncateString } from '../../../helpers/stringHelpers';

import BasicChip from '../../display/chips/basicChip';
import BasicChipsList from '../../display/chips/basicChipsList';
// components
import { SearchableSelectType } from '../../inputs/searchableSelect';
//context
import { UseTitleContext } from './FilterTitleContext';

interface FilterValueContainerProps
    extends ValueContainerProps<
        SearchableSelectType,
        boolean,
        GroupBase<SearchableSelectType>
    > {
    showChips?: boolean;
    removeChipHandler?: (id: string | number) => void;
}
const FilterValueContainer: React.FC<
    React.PropsWithChildren<FilterValueContainerProps>
> = ({
    getValue,
    options,
    children,
    showChips,
    removeChipHandler,
    ...rest
}) => {
    const [, input]: any = children;
    const title = useContext(UseTitleContext);

    const [text, setText] = useState<string>('');
    const containerRef = useRef<HTMLDivElement>(null);
    const value = getValue();
    const [parentWidth, setParentWidth] = useState<number>(0);
    const updateParentWidth = () => {
        if (containerRef.current) {
            const closestParent = containerRef.current.closest(
                '[class*="control"]',
            ) as HTMLElement;
            if (closestParent) {
                setParentWidth(closestParent.offsetWidth);
            }
        }
    };
    // Update the parent width when the component mounts or updates
    useEffect(() => {
        updateParentWidth();
        const observer = new ResizeObserver(updateParentWidth);
        if (containerRef.current) {
            const closestParent =
                containerRef.current.closest('[class*="control"]');
            if (closestParent) {
                observer.observe(closestParent);
            }
        }
        return () => {
            observer.disconnect();
        };
    }, []);

    // Calculate character count based on parent container width

    const characterCount = useMemo(() => {
        const getCharacterCount = () => {
            if (parentWidth >= 400) return 39;
            if (parentWidth >= 350) return 35;
            if (parentWidth >= 320) return 32;
            if (parentWidth >= 300) return 29;
            if (parentWidth >= 280) return 27;
            if (parentWidth >= 240) return 23;
            if (parentWidth >= 200) return 20;
            if (parentWidth > 160) return 16;
            if (parentWidth > 60) return 8;
            return 7;
        };
        return getCharacterCount();
    }, [parentWidth]);

    useEffect(() => {
        if (input.props.value) {
            setText('');
        } else {
            const firstLabel = value[0];
            if (value.length > 1) {
                setText(`${value.length} selected`);
            } else if (value.length === 1 && firstLabel.value !== 0) {
                setText(truncateString(firstLabel.label, characterCount));
            } else {
                setText(`All (${options.length})`);
            }
        }
    }, [value, options, characterCount, input.props.value]);

    const chipValues = useMemo<
        {
            id: string | number;
            name: string;
        }[]
    >(() => {
        return value.map((valObj) => ({
            id: valObj.value,
            name: valObj.label,
        }));
    }, [value]);

    return (
        <div style={{ width: '100%' }} ref={containerRef}>
            <components.ValueContainer
                {...rest}
                getValue={getValue}
                options={options}
            >
                <Content>
                    {showChips && (
                        <ChipsContainer>
                            <BasicChipsList
                                style={{
                                    marginTop: `${aampeTheme.spacing(0)}`,
                                }}
                                items={chipValues.slice(0, 3)}
                                onClickHandler={removeChipHandler}
                            />
                            {chipValues.length - 3 > 0 ? (
                                <BasicChip
                                    title={`+${chipValues.length - 3} more`}
                                />
                            ) : (
                                ''
                            )}
                        </ChipsContainer>
                    )}
                    {title ?? text}
                    {input}
                    <FilterContent>
                        {!!value.length && !showChips && (
                            <Text>{value.length}</Text>
                        )}
                    </FilterContent>
                </Content>
            </components.ValueContainer>
        </div>
    );
};

const Content = styled.div({
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
    justifyContent: 'space-between',
    width: '100%',
});

const Text = styled.span({
    borderRadius: aampeTheme.borderRadius.default,
    backgroundColor: aampeTheme.colors.solidA01,
    padding: aampeTheme.spacing4(1),
    color: aampeTheme.colors.white,
    fontSize: aampeTheme.size.font.textSmall,
    fontWeight: aampeTheme.weights.light,
    lineHeight: aampeTheme.size.lineHeight.default,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
});
const FilterContent = styled.div({
    width: '20px',
    height: '20px',
});

const ChipsContainer = styled.div({
    display: 'flex',
    alignItems: 'center',
    gap: `${aampeTheme.spacing4(3)}`,
});
export default FilterValueContainer;
